import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useMotionValue, useReducedMotion, animate as fmAnimate } from "framer-motion";
import { FRAMEWORKS, HERO_TOPICS, EVIDENCE } from "@/lib/data";
import { IDS } from "@/lib/ids";
import Constellation from "./Constellation";

// ============ Living Crosswalk topology (SVG) =====================

const R_TOPIC = 155;
const R_FWK = 330;

const angleAt = (i, n, offsetDeg = -90) => ((offsetDeg + (i / n) * 360) * Math.PI) / 180;

const TOPIC_POS = HERO_TOPICS.map((t, i) => {
  const a = angleAt(i, HERO_TOPICS.length);
  return { ...t, x: Math.cos(a) * R_TOPIC, y: Math.sin(a) * R_TOPIC, a };
});

const FWK_POS = FRAMEWORKS.map((f, i) => {
  const a = angleAt(i, FRAMEWORKS.length);
  return { ...f, x: Math.cos(a) * R_FWK, y: Math.sin(a) * R_FWK, a };
});

// Assign each of an evidence's frameworks to one of its topics (round-robin)
function computeTopicToFwkMap(evidence) {
  const map = new Map();
  evidence.topics.forEach((t) => map.set(t, []));
  evidence.frameworks.forEach((f, i) => {
    const t = evidence.topics[i % evidence.topics.length];
    map.get(t).push(f);
  });
  return map;
}

// Quantize a duration to nearest arrival beat (~180ms slots)
const BEAT = 180;
const quantize = (ms) => Math.round(ms / BEAT) * BEAT;

// ============ Counter — rAF-bound via framer motion values ========

function AnimCounter({ value, testId, className }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const ctrl = fmAnimate(mv, value, {
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => ctrl.stop();
  }, [value, mv]);

  return (
    <span data-testid={testId} className={`font-mono tabular ${className}`} aria-live="polite">
      {display}
    </span>
  );
}

// =================================================================

export default function Hero() {
  const [coveredTopics, setCoveredTopics] = useState(new Set());
  const [coveredFrameworks, setCoveredFrameworks] = useState(new Set());
  const [activeEdges, setActiveEdges] = useState([]); // {id, from:{x,y}, to:{x,y}, delay, life}
  const [stack, setStack] = useState([]); // evidence ids in the coverage stack
  const [hoverFwk, setHoverFwk] = useState(null);
  const [hoverTopic, setHoverTopic] = useState(null);
  const [spotlight, setSpotlight] = useState({ x: 50, y: 50, visible: false });

  const heroRef = useRef(null);

  // Compute cross-relations across ALL evidence for hover reveal.
  // Framework -> topics: every topic mapped by any evidence that satisfies this framework.
  const fwkToTopics = useMemo(() => {
    const m = new Map();
    EVIDENCE.forEach((ev) => {
      ev.frameworks.forEach((f) => {
        if (!m.has(f)) m.set(f, new Set());
        ev.topics.forEach((t) => m.get(f).add(t));
      });
    });
    return m;
  }, []);

  // Topic -> frameworks: every framework mapped by any evidence that touches this topic.
  const topicToFwks = useMemo(() => {
    const m = new Map();
    EVIDENCE.forEach((ev) => {
      ev.topics.forEach((t) => {
        if (!m.has(t)) m.set(t, new Set());
        ev.frameworks.forEach((f) => m.get(t).add(f));
      });
    });
    return m;
  }, []);

  // dedupe count for the counter (union of frameworks; sum of controls with dedup)
  const counter = useMemo(() => {
    const fwks = new Set();
    let controls = 0;
    stack.forEach((id) => {
      const e = EVIDENCE.find((x) => x.id === id);
      if (!e) return;
      e.frameworks.forEach((f) => fwks.add(f));
      controls += e.counts.controls;
    });
    return { evidence: stack.length, controls, frameworks: fwks.size };
  }, [stack]);

  const coveragePct = useMemo(() => {
    return Math.min(100, Math.round((counter.frameworks / 20) * 100));
  }, [counter.frameworks]);

  const edgeIdRef = useRef(0);
  const timeoutsRef = useRef([]);

  const clearTimers = () => {
    timeoutsRef.current.forEach((t) => clearTimeout(t));
    timeoutsRef.current = [];
  };
  useEffect(() => () => clearTimers(), []);

  const reset = () => {
    clearTimers();
    setCoveredTopics(new Set());
    setCoveredFrameworks(new Set());
    setActiveEdges([]);
    setStack([]);
  };

  const fire = (ev) => {
    // Add to stack (dedupe)
    setStack((s) => (s.includes(ev.id) ? s : [...s, ev.id]));

    const map = computeTopicToFwkMap(ev);

    // 1. Draw edges from evidence -> topics
    const topicPositions = ev.topics
      .map((tid) => TOPIC_POS.find((t) => t.id === tid))
      .filter(Boolean);

    const newEdges = [];
    topicPositions.forEach((t, i) => {
      newEdges.push({
        id: `ev-t-${edgeIdRef.current++}`,
        from: { x: 0, y: 0 },
        to: { x: t.x, y: t.y },
        delay: i * 40,
        duration: 380,
        hue: "blue",
      });
    });

    // 2. Topic -> framework edges (from map)
    topicPositions.forEach((t) => {
      const fs = map.get(t.id) || [];
      fs.forEach((fid) => {
        const f = FWK_POS.find((x) => x.id === fid);
        if (!f) return;
        const dx = f.x - t.x, dy = f.y - t.y;
        const dist = Math.hypot(dx, dy);
        // travel time proportional to distance, quantized
        const raw = 260 + (dist / R_FWK) * 380;
        const travel = quantize(raw);
        newEdges.push({
          id: `t-f-${edgeIdRef.current++}`,
          from: { x: t.x, y: t.y },
          to: { x: f.x, y: f.y },
          delay: 320,
          duration: travel,
          hue: "grad",
          arrivesFwkId: fid,
        });
      });
    });

    setActiveEdges((prev) => [...prev, ...newEdges]);

    // 3. Schedule topic ignition (fires with edge arrival ~380ms)
    ev.topics.forEach((tid, i) => {
      timeoutsRef.current.push(
        setTimeout(() => {
          setCoveredTopics((s) => new Set([...s, tid]));
        }, 300 + i * 40)
      );
    });

    // 4. Schedule framework ignition on pulse arrival
    newEdges.forEach((e) => {
      if (!e.arrivesFwkId) return;
      timeoutsRef.current.push(
        setTimeout(() => {
          setCoveredFrameworks((s) => new Set([...s, e.arrivesFwkId]));
        }, e.delay + e.duration - 40)
      );
    });

    // 5. cleanup transient traveling pulses after they finish (but leave "persistent" thin lines behind implicitly via node lit state)
    timeoutsRef.current.push(
      setTimeout(() => {
        setActiveEdges((prev) => prev.filter((edge) => !newEdges.find((n) => n.id === edge.id) || edge.persistent));
      }, 1400)
    );
  };

  // Auto-demo on load
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      // instant end-state
      const first = EVIDENCE[0];
      setStack([first.id]);
      setCoveredTopics(new Set(first.topics));
      setCoveredFrameworks(new Set(first.frameworks));
      return;
    }
    const t = setTimeout(() => fire(EVIDENCE[0]), 1400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);

  // Cursor spotlight — subtle radial glow following mouse in hero
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setSpotlight({ x, y, visible: true });
    };
    const onLeave = () => setSpotlight((s) => ({ ...s, visible: false }));
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <section ref={heroRef} id="top" className="relative overflow-hidden min-h-[100svh] grid place-content-center pt-24 pb-10 md:pt-28 md:pb-14">
      {/* Cursor spotlight */}
      <div
        className="absolute inset-0 pointer-events-none z-0 transition-opacity duration-500"
        style={{
          opacity: spotlight.visible ? 1 : 0,
          background: `radial-gradient(600px circle at ${spotlight.x}% ${spotlight.y}%, rgba(96,165,250,0.09), transparent 55%)`,
        }}
        aria-hidden
      />
      {/* Ambient constellation (cool only) — background layer */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <Constellation density={54} />
      </div>
      {/* subtle dot grid */}
      <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none z-0" />
      {/* radial dim */}
      <div className="absolute inset-0 pointer-events-none z-0" style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(6,8,18,0) 0%, rgba(6,8,18,0.45) 60%, rgba(6,8,18,0.9) 100%)" }} />

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-10">
        {/* headline block — left aligned editorial */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-ink-muted">
                <span className="w-1 h-1 rounded-full bg-brand-blue" />
                CH · 00 · Living Crosswalk
              </span>
              <span className="h-px w-10 bg-white/15" />
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">Now onboarding design partners</span>
            </div>

            {/* Masked line-by-line reveal */}
            <h1 className="font-display text-[40px] sm:text-[52px] md:text-[58px] lg:text-[64px] xl:text-[72px] leading-[1.02] tracking-[-0.022em] text-balance">
              <MaskLine delay={0.05}>Collect once.</MaskLine>
              <MaskLine delay={0.15}>Satisfy <span className="grad-text">every</span> framework.</MaskLine>
            </h1>

            {/* Inline chain — the single strongest proof line */}
            <div className="mt-5 flex flex-wrap items-baseline gap-x-2 gap-y-1 font-mono text-[13px] md:text-[14px] tabular text-white/85">
              <ChainStat testId={IDS.heroCounterEvidence} value={counter.evidence} label="evidence" />
              <span className="text-white/40 mx-1" aria-hidden>→</span>
              <ChainStat testId={IDS.heroCounterControls} value={counter.controls} label="controls" />
              <span className="text-white/40 mx-1" aria-hidden>→</span>
              <ChainStat testId={IDS.heroCounterFrameworks} value={counter.frameworks} label="frameworks" suffix="/ 20" />
            </div>

            <p className="mt-5 max-w-[52ch] text-ink-muted text-[14.5px] md:text-[16px] leading-relaxed">
              NormShift sits on a canonical layer of ~68 security &amp; privacy topics beneath 20 frameworks.
              Attach evidence <em className="not-italic text-ink-text">once</em> — it satisfies every equivalent control across every enabled standard, at the same time.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a href="#demo" data-testid={IDS.heroCta} className="cta">
                Request a demo
                <span aria-hidden>→</span>
              </a>
              <a href="#pricing" data-testid={IDS.heroSecondaryCta} className="cta-secondary">
                Start ISO 27001 free
              </a>
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/45 ml-1">
                ISO&nbsp;27001 · <span className="text-brand-red">Free</span>
              </span>
            </div>

            {/* coverage meter — clearly labeled as live demo output */}
            <div className="mt-6 w-full max-w-[380px]">
              <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">
                <span>Live coverage · this demo</span>
                <span className="tabular">{coveragePct}%</span>
              </div>
              <div className="mt-2 h-[3px] w-full bg-white/6 relative overflow-hidden rounded-full">
                <motion.div
                  className="absolute inset-y-0 left-0 grad-bg rounded-full"
                  animate={{ width: `${coveragePct}%` }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </div>
          </div>

          {/* Right column — the topology */}
          <div className="lg:col-span-6 relative">
            <div className="relative aspect-square max-w-[560px] xl:max-w-[620px] mx-auto pointer-events-auto">
              <TopologySVG
                coveredTopics={coveredTopics}
                coveredFrameworks={coveredFrameworks}
                activeEdges={activeEdges}
                hoverFwk={hoverFwk}
                setHoverFwk={setHoverFwk}
                hoverTopic={hoverTopic}
                setHoverTopic={setHoverTopic}
                fwkToTopics={fwkToTopics}
                topicToFwks={topicToFwks}
              />
            </div>

            {/* Evidence rail */}
            <div className="mt-4 pointer-events-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">Drop evidence · click to fire</span>
                <button
                  data-testid={IDS.heroResetButton}
                  onClick={reset}
                  className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted hover:text-ink-text transition-colors focus-visible:outline-2 focus-visible:outline-brand-blue"
                >
                  ↺ Reset
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {EVIDENCE.map((e) => {
                  const active = stack.includes(e.id);
                  return (
                    <button
                      type="button"
                      key={e.id}
                      data-testid={IDS.evidenceChip(e.id)}
                      onClick={() => fire(e)}
                      className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition-[transform,border-color,background] duration-300 hover:-translate-y-[3px] hover:border-white/25 ${active ? "border-brand-red/50 bg-[rgba(232,37,50,0.06)]" : "border-white/12 bg-white/[0.02]"}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-brand-red" : "bg-white/40"}`} />
                      <span className="font-mono text-[10.5px] tracking-[0.06em]">{e.file}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 font-mono text-[9.5px] tracking-[0.16em] uppercase text-white/35">
                Tip · <span className="lg:hidden">tap an evidence file to watch it satisfy every framework</span><span className="hidden lg:inline">hover any framework or topic to trace its connections</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ Masked line reveal =============================
function MaskLine({ children, delay = 0 }) {
  const reduced = useReducedMotion();
  return (
    <span className="block overflow-hidden">
      <motion.span
        initial={reduced ? { y: "0%" } : { y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: reduced ? 0 : 0.45, delay: reduced ? 0 : delay, ease: [0.22, 1, 0.36, 1] }}
        className="block will-change-transform"
      >
        {children}
      </motion.span>
    </span>
  );
}

// ============ Chain stat (inline number + label) ==============
function ChainStat({ value, label, suffix, testId }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <AnimCounter value={value} testId={testId} className="text-[22px] md:text-[26px] font-display leading-none text-white" />
      {suffix && <span className="text-white/40 text-[10px]">{suffix}</span>}
      <span className="text-white/60 text-[11px] tracking-[0.06em] uppercase ml-0.5">{label}</span>
    </span>
  );
}

// ============ Counter column ==================================
function CounterCol({ label, value, testId, suffix }) {
  return (
    <div className="flex flex-col">
      <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-ink-muted">{label}</span>
      <span className="mt-1 flex items-baseline gap-2">
        <AnimCounter value={value} testId={testId} className="text-[36px] md:text-[44px] font-display leading-none" />
        {suffix && <span className="font-mono text-[10px] tracking-[0.18em] text-ink-muted">{suffix}</span>}
      </span>
    </div>
  );
}

// ============ SVG topology =====================================
function TopologySVG({ coveredTopics, coveredFrameworks, activeEdges, hoverFwk, setHoverFwk, hoverTopic, setHoverTopic, fwkToTopics, topicToFwks }) {
  const [isMobile, setIsMobile] = useState(false); useEffect(() => { const mq = window.matchMedia("(max-width: 1023px)"); const on = () => setIsMobile(mq.matches); on(); mq.addEventListener("change", on); return () => mq.removeEventListener("change", on); }, []); // Compute hover-revealed edges (framework <-> its topics, or topic <-> its frameworks)
  const hoverEdges = [];
  let hoverTooltip = null;
  if (hoverFwk) {
    const f = FWK_POS.find((x) => x.id === hoverFwk);
    const ts = fwkToTopics.get(hoverFwk) || new Set();
    ts.forEach((tid) => {
      const t = TOPIC_POS.find((x) => x.id === tid);
      if (!t) return;
      hoverEdges.push({ from: { x: t.x, y: t.y }, to: { x: f.x, y: f.y }, key: `hf-${tid}` });
    });
    hoverTooltip = { text: `Satisfied by ${ts.size} shared topic${ts.size === 1 ? "" : "s"}`, x: f.x, y: f.y };
  } else if (hoverTopic) {
    const t = TOPIC_POS.find((x) => x.id === hoverTopic);
    const fs = topicToFwks.get(hoverTopic) || new Set();
    fs.forEach((fid) => {
      const f = FWK_POS.find((x) => x.id === fid);
      if (!f) return;
      hoverEdges.push({ from: { x: t.x, y: t.y }, to: { x: f.x, y: f.y }, key: `ht-${fid}` });
    });
    hoverTooltip = { text: `Feeds ${fs.size} framework${fs.size === 1 ? "" : "s"}`, x: t.x, y: t.y };
  }

  const isDimmed = !!(hoverFwk || hoverTopic);
  const relatedFwks = hoverTopic ? (topicToFwks.get(hoverTopic) || new Set()) : null;
  const relatedTopics = hoverFwk ? (fwkToTopics.get(hoverFwk) || new Set()) : null;

  return (
    <svg viewBox={isMobile ? "-360 -360 720 720" : "-420 -420 840 840"} className="w-full h-full" role="img" aria-label="Living crosswalk topology">
      <defs>
        <linearGradient id="gradStroke" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#60A5FA" />
          <stop offset="50%" stopColor="#6366F1" />
          <stop offset="100%" stopColor="#E82532" />
        </linearGradient>
        <radialGradient id="evGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#E82532" stopOpacity="0.5" />
          <stop offset="60%" stopColor="#E82532" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#E82532" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="topicGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#6366F1" stopOpacity="0.5" />
          <stop offset="60%" stopColor="#6366F1" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
        </radialGradient>
        <filter id="softBloom" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" />
        </filter>
      </defs>

      {/* faint concentric rings */}
      <circle cx="0" cy="0" r={R_TOPIC} fill="none" stroke="var(--hairline)" strokeDasharray="2 4" />
      <circle cx="0" cy="0" r={R_FWK} fill="none" stroke="var(--hairline)" strokeDasharray="2 4" />

      {/* hover reveal edges (persistent while hovered) */}
      {hoverEdges.map((e) => (
        <line
          key={e.key}
          x1={e.from.x} y1={e.from.y}
          x2={e.to.x}   y2={e.to.y}
          stroke="rgba(96,165,250,0.7)"
          strokeWidth="1"
          strokeLinecap="round"
        />
      ))}

      {/* active traveling edges */}
      {activeEdges.map((e) => (
        <TravelingEdge key={e.id} edge={e} />
      ))}

      {/* topic nodes */}
      {TOPIC_POS.map((t) => {
        const lit = coveredTopics.has(t.id);
        const isRelated = relatedTopics ? relatedTopics.has(t.id) : (hoverTopic === t.id ? true : !isDimmed);
        const opacity = isDimmed ? (isRelated ? 1 : 0.22) : 1;
        return (
          <g
            key={t.id}
            transform={`translate(${t.x} ${t.y})`}
            onPointerEnter={() => setHoverTopic(t.id)}
            onPointerLeave={() => setHoverTopic(null)}
            style={{ opacity, transition: "opacity 200ms" }}
          >
            {lit && <circle r={isMobile ? 34 : 30} fill="url(#topicGlow)" />}
            <motion.circle
              r={(lit ? 6 : 4) * (isMobile ? 1.4 : 1)}
              initial={false}
              animate={{ scale: lit ? 1 : 0.85 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              fill={lit ? "#6366F1" : "rgba(255,255,255,0.15)"}
              stroke={lit ? "#8B92FF" : "rgba(255,255,255,0.25)"}
              strokeWidth="1"
              style={{ cursor: "pointer" }}
            />
            <text
              y={t.y > 30 ? 22 : -14}
              textAnchor="middle"
              className={`font-mono ${lit ? "label-lit" : "label-dim"}`}
              style={{ fontSize: "10px", letterSpacing: "0.06em", pointerEvents: "none", display: isMobile ? "none" : undefined }}
            >
              {t.label}
            </text>
          </g>
        );
      })}

      {/* framework nodes */}
      {FWK_POS.map((f) => {
        const lit = coveredFrameworks.has(f.id);
        const isRelated = relatedFwks ? relatedFwks.has(f.id) : (hoverFwk === f.id ? true : !isDimmed);
        const opacity = isDimmed ? (isRelated ? 1 : 0.2) : 1;
        // Label position — angle-aware for readability
        const labelR = R_FWK + 34;
        const lx = Math.cos(f.a) * labelR;
        const ly = Math.sin(f.a) * labelR;
        const anchor = f.x > 20 ? "start" : f.x < -20 ? "end" : "middle";
        return (
          <g
            key={f.id}
            transform={`translate(${f.x} ${f.y})`}
            onPointerEnter={() => setHoverFwk(f.id)}
            onPointerLeave={() => setHoverFwk(null)}
            style={{ opacity, transition: "opacity 200ms", cursor: "pointer" }}
          >
            {lit && <circle r="18" fill="url(#topicGlow)" style={{ filter: "hue-rotate(-30deg) saturate(0.7)" }} />}
            <motion.rect
              x="-6" y="-6" width="12" height="12"
              className={lit ? "node-lit" : "node-idle"}
              strokeWidth={lit ? 1.5 : 1}
              initial={false}
              animate={{ rotate: 45, scale: (lit ? 1.15 : 1) * (isMobile ? 1.5 : 1) }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              data-testid={IDS.frameworkNode(f.id)}
            />
            {/* transparent hit area for easier hover */}
            <circle r="18" fill="transparent" />
            <text
              x={lx - f.x}
              y={ly - f.y + 3}
              textAnchor={anchor}
              className={`font-mono ${lit ? "label-lit" : "label-dim"}`}
              style={{ fontSize: "9.5px", letterSpacing: "0.03em", pointerEvents: "none", display: isMobile ? "none" : undefined }}
            >
              {f.name}
            </text>
            <text
              x={lx - f.x}
              y={ly - f.y + 15}
              textAnchor={anchor}
              className="label-dim font-mono"
              style={{ fontSize: "8px", letterSpacing: "0.04em", opacity: 0.55, pointerEvents: "none", display: isMobile ? "none" : undefined }}
            >
              {f.code}
            </text>
          </g>
        );
      })}

      {/* evidence core */}
      <g style={{ opacity: isDimmed ? 0.5 : 1, transition: "opacity 200ms" }}>
        <circle r="60" fill="url(#evGlow)" />
        <motion.circle
          r="16"
          fill="#E82532"
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
        />
        <circle r="16" fill="none" stroke="rgba(232,37,50,0.5)" strokeWidth="1" />
        <text
          y={isMobile ? 56 : 42}
          textAnchor="middle"
          className="font-mono"
          style={{ fontSize: isMobile ? "20px" : "10px", fill: "var(--text)", letterSpacing: "0.04em" }}
        >
          evidence
        </text>
      </g>

      {/* hover tooltip */}
      {hoverTooltip && (
        <g transform={`translate(${hoverTooltip.x} ${hoverTooltip.y - 30})`} style={{ pointerEvents: "none" }}>
          <rect
            x="-70"
            y="-14"
            width="140"
            height="20"
            rx="4"
            fill="var(--panel)"
            stroke="var(--hairline-strong)"
            strokeWidth="0.8"
          />
          <text
            textAnchor="middle"
            y="0"
            className="font-mono"
            style={{ fontSize: "9px", fill: "var(--text)", letterSpacing: "0.06em" }}
          >
            {hoverTooltip.text}
          </text>
        </g>
      )}
    </svg>
  );
}

// ============ Traveling edge (SVG path w/ pulse dot) ===========
function TravelingEdge({ edge }) {
  const strokeUrl = edge.hue === "grad" ? "url(#gradStroke)" : "rgba(96,165,250,0.55)";
  return (
    <g>
      {/* trail */}
      <motion.line
        x1={edge.from.x} y1={edge.from.y}
        x2={edge.to.x}   y2={edge.to.y}
        stroke={strokeUrl}
        strokeWidth="1"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: [0, 1, 0.35] }}
        transition={{ duration: edge.duration / 1000, delay: edge.delay / 1000, ease: [0.22,1,0.36,1] }}
      />
      {/* moving dot */}
      <motion.circle
        r="2.5"
        fill={edge.hue === "grad" ? "#E82532" : "#60A5FA"}
        initial={{ cx: edge.from.x, cy: edge.from.y, opacity: 0 }}
        animate={{ cx: edge.to.x, cy: edge.to.y, opacity: [0, 1, 0] }}
        transition={{ duration: edge.duration / 1000, delay: edge.delay / 1000, ease: [0.22,1,0.36,1] }}
      />
    </g>
  );
}
